import {fabric} from 'fabric'

export default class Editor {
    options = {}
    mainCanvas = null

    image = null
    maskFilter = null

    cursor = null
    cursorCanvas = null

    constructor(opts) {
        this.options.canvas = opts.canvas
        this.options.img = opts.img
        this.options.mask = opts.mask
        this.options.width = opts.width
        this.options.height = opts.height
        this.options.cursor = opts.cursor
    }

    async createEditor() {
        // custom brush add to fabric.js for masking
        await this.addCustomBrushToFabric()

        // create main canvas
        this.mainCanvas = new fabric.Canvas(this.options.canvas, {
            preserveObjectStacking: true,
            controlsAboveOverlay: true,
            width: this.options.width,
            height: this.options.height,
            freeDrawingCursor: 'none'
        })

        // create fabric circle for using as a cursor
        this.cursorCanvas = new fabric.StaticCanvas(this.options.cursor, {
            width: this.mainCanvas.width,
            height: this.mainCanvas.height
        })
        await this.setCursor()

        // create masked image
        await this.createMaskedCanvas()

        // create freeDrawingBrush with custom brush
        this.mainCanvas.freeDrawingBrush = new fabric.MaskBrush({
            canvas: this.mainCanvas,
            target: this.image,
            mode: 'source-out',
            targetMaskFilter: this.maskFilter,
            width: 25
        })

        return this
    }

    setCursor() {
        const vm = this
        const cursorOpacity = .5;

        this.mouseCursor = new fabric.Circle({
            left: -100,
            top: -100,
            radius: 25 / 2,
            fill: "rgba(96,96,96, " + cursorOpacity + ")",
            stroke: "#e3e3e3",
            originX: 'center',
            originY: 'center'
        })

        this.mainCanvas.on('mouse:move', function (evt) {
            if (vm.mainCanvas.isDrawingMode) {
                const mouse = this.getPointer(evt.e);
                vm.mouseCursor
                    .set({
                        top: mouse.y,
                        left: mouse.x
                    })
                    .setCoords()
                    .canvas.renderAll();
            }
        });

        this.mainCanvas.on('mouse:out', function () {
            if (vm.mainCanvas.isDrawingMode) {
                // put circle off screen
                vm.mouseCursor
                    .set({
                        top: vm.mouseCursor.top,
                        left: vm.mouseCursor.left
                    })
                    .setCoords()
                    .canvas.renderAll();
            }
        });
    }

    changeBackgroundColor(id, color) {
        const currentObject = this.findObject(id)
        this.mainCanvas.remove(currentObject)
        this.addRect(color)
    }

    makeBlurredBg(blur) {
        this.removeBackground()
        this.addBackgroundImage(this.options.img, blur)
    }

    addBackgroundImage(img, blur = null) {
        const bgImg = new fabric.Image(img)

        bgImg.set({id: 'background'})

        bgImg.scaleToHeight(this.options.height)
        bgImg.scaleToWidth(this.options.width)

        if (blur && blur > 0) {
            bgImg.filters.push(new fabric.Image.filters.Blur({
                blur
            }))
            bgImg.applyFilters()
        }

        this.mainCanvas.add(bgImg)
        this.mainCanvas.sendToBack(bgImg)
        this.mainCanvas.viewportCenterObject(bgImg)
    }

    addRect(color) {
        const rect = new fabric.Rect({
            id: 'background',
            fill: color ?? '#ffffff00',
            width: this.mainCanvas.width,
            height: this.mainCanvas.height,
            selectable: false
        });
        this.mainCanvas.add(rect);
        this.mainCanvas.sendToBack(rect)
    }

    findObject(id) {
        let object = null
        this.mainCanvas.getObjects().forEach(function (o) {
            if (o.id === id) {
                object = o
            }
        })
        return object
    }

    setDrawingMode(mode) {
        if (mode) {
            if (this.cursorCanvas._objects.length < 1) {
                this.cursorCanvas.add(this.mouseCursor)
            }
            this.setFreeDrawing(true)
            this.mainCanvas.freeDrawingBrush.mode = mode
        } else {
            this.setFreeDrawing(false)
            this.cursorCanvas.remove(this.mouseCursor)
        }
    }

    setBrushSize(size) {
        this.mouseCursor.center()
            .set({
                radius: size / 2
            })
            .setCoords()
            .canvas.renderAll();

        this.mainCanvas.freeDrawingBrush.width = size
    }

    setFreeDrawing(draw) {
        const activeObject = this.findObject('content')
        activeObject.selectable = !draw;
        this.mainCanvas.isDrawingMode = draw
    }

    removeBackground() {
        const currentObject = this.findObject('background')
        this.mainCanvas.remove(currentObject)
    }

    async createMaskedCanvas() {
        this.image = new fabric.Image(this.options.img)
        const fMask = new fabric.Image(this.options.mask)

        this.image.set({id: 'content'})

        this.image.scaleToHeight(this.options.height)
        this.image.scaleToWidth(this.options.width)

        this.maskFilter = new fabric.Image.filters.BlendImage({
            image: fMask,
            mode: 'mask',
            alpha: 1
        })

        this.image.filters.push(this.maskFilter)
        this.image.applyFilters()

        this.mainCanvas.add(this.image)
        this.mainCanvas.renderAll()
    }

    changeBackground(id, color) {
        const currentObject = this.findObject(id)
        this.mainCanvas.remove(currentObject)
        this.addRect(color)
    }

    zoomDefault() {
        this.mainCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        this.cursorCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

        this.mainCanvas.renderAll()
        this.cursorCanvas.renderAll()
    }

    async downloadImg() {
        this.zoomDefault()
        const scale = this.image.width / this.mainCanvas.getWidth()

        const base64 = this.mainCanvas.toDataURL({
            multiplier: scale,
            format: "png"
        })

        const a = document.createElement("a")
        a.href = base64
        a.download = `Image-${Date.now()}.png`
        a.click()
    }

    async addCustomBrushToFabric() {
        fabric.Image.filters.BlendImage.prototype.mode = "mask";
        fabric.Image.fromURL("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQYV2NgYGD4DwABBAEAcCBlCwAAAABJRU5ErkJggg==", function (img) {
            fabric.Image.filters.BlendImage.prototype.image = img;
        })

        fabric.Image.filters.BlendImage.prototype.fragmentSource["mask"] = `
        precision highp float;
		uniform sampler2D uTexture;
		uniform sampler2D uImage;
		uniform vec4 uColor;
		varying vec2 vTexCoord;
		varying vec2 vTexCoord2;

		void main() {
			vec4 color = texture2D(uTexture, vTexCoord);
			vec4 color2 = texture2D(uImage, vTexCoord2);
			color.a *= color2.a;
			gl_FragColor = color;
		}`;


        fabric.MaskBrush = fabric.util.createClass(fabric.PencilBrush, {
            initialize: function (options) {
                this.canvas = options.canvas;
                this.width = options.width;
                this.target = options.target;
                this.shadow = options.shadow;
                this.blur = options.blur;
                this.color = options.color;
                this.targetMaskFilter = options.targetMaskFilter;
                //"destination-over" ADD
                //"source-out" REMOVE
                this.mode = options.mode ? options.mode : "source-out";
                this._points = [];

            },
            _saveAndTransform: function (ctx) {
                var self = this;
                var v = this.canvas.viewportTransform;
                ctx.save();
                ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
                ctx.filter = self.blur ? "blur(" + self.blur + "px)" : "none";
            },
            _finalizeAndAddPath: function () {
                var self = this;
                var ctx = this.canvas.contextTop;


                ctx.closePath();
                if (this.decimate) {
                    this._points = this.decimatePoints(this._points, this.decimate);
                }
                var pathData = this.convertPointsToSVGPath(this._points).join('');
                if (pathData === 'M 0 0 Q 0 0 0 0 L 0 0') {
                    // do not create 0 width/height paths, as they are
                    // rendered inconsistently across browsers
                    // Firefox 4, for example, renders a dot,
                    // whereas Chrome 10 renders nothing
                    this.canvas.requestRenderAll();
                    return;
                }

                var r = this.target.getBoundingRect();
                var imgData = this.canvas.contextTop.getImageData(r.left, r.top, r.width, r.height);

                var c = document.createElement("canvas");
                var tempCtx = c.getContext("2d");
                c.width = r.width;
                c.height = r.height;
                if (!self.targetMaskFilter.image) {
                    tempCtx.fillRect(0, 0, c.width, c.height);
                    fabric.Image.fromURL(c.toDataURL(), function (mask) {
                        self.targetMaskFilter.image = mask;
                        self.target.applyFilters();
                        self.canvas.requestRenderAll();
                        self._finalizeAndAddPath();
                    });

                    return;
                }


                tempCtx.putImageData(imgData, 0, 0);
                if (self.targetMaskFilter.image) {
                    tempCtx.globalCompositeOperation = self.mode;
                    tempCtx.drawImage(self.targetMaskFilter.image.getElement(), 0, 0, c.width, c.height);
                }
                fabric.Image.fromURL(c.toDataURL(), function (mask) {
                    self.targetMaskFilter.image = mask;
                    self.target.applyFilters();
                    self.canvas.requestRenderAll();
                });
                this.canvas.clearContext(this.canvas.contextTop);
                this.canvas.requestRenderAll();
                this._resetShadow();
            }
        })
    }
}